// src/lib/sam-api/validator.ts
import { SamContractResponse, SamContractLocation } from '../../types/sam';

export class SamDataValidator {
  static validateContract(data: Partial<SamContractResponse>): { 
    isValid: boolean; 
    errors: string[]; 
    warnings: string[] 
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for notice_id - this is CRITICAL
    const noticeId = data.notice_id || data.noticeId;
    if (!noticeId || (typeof noticeId === 'string' && noticeId.trim() === '')) {
      console.error(`❌ CRITICAL: Contract is missing notice_id. Contract Data:`, data);
      errors.push('Missing required field: notice_id');
      return { isValid: false, errors, warnings };
    }

    console.log(`✅ Validating contract: ${noticeId}`);

    // Check for other required fields - these are warnings, not errors
    if (!data.title) {
      warnings.push('Missing title');
    }
    
    const naicsCode = data.naicsCode || data.naics_code;
    if (!naicsCode) {
      warnings.push('Missing NAICS code');
    }
    
    const agency = data.agency;
    if (!agency) {
      warnings.push('Missing agency');
    }
    
    if (!data.status) {
      warnings.push('Missing status');
    }
    
    const postedDate = data.postedDate || data.posted_date;
    if (!postedDate) {
      warnings.push('Missing posted date');
    }
    
    if (!data.description) {
      warnings.push('Missing description');
    }

    // Validate date formats if present
    this.validateDates(data, warnings);
    
    // Validate numeric fields if present
    this.validateNumericFields(data, warnings);
    
    // Validate NAICS code format if present
    this.validateNaicsCode(naicsCode, warnings);
    
    // Validate location data if present
    const agencyLocation = typeof data.agency === 'object' ? data.agency?.location : null;
    this.validateLocation(agencyLocation, 'Agency location', warnings);
    this.validateLocation(data.placeOfPerformance, 'Place of performance', warnings);

    const isValid = errors.length === 0;
    
    if (isValid) {
      console.log(`✅ Contract ${noticeId} passed validation with ${warnings.length} warnings`);
    } else {
      console.error(`❌ Contract ${noticeId} failed validation:`, errors);
    }

    return {
      isValid,
      errors,
      warnings
    };
  }

  private static validateDates(data: Partial<SamContractResponse>, warnings: string[]) {
    const dateFields = [
      { field: 'postedDate', value: data.postedDate || data.posted_date },
      { field: 'updatedPublishedDate', value: data.updatedPublishedDate || data.updated_published_date },
      { field: 'responseDeadline', value: data.dueDate || data.due_date },
      { field: 'awardDate', value: data.awardDate || data.award_date },
      { field: 'questionsDeadline', value: data.questionsDeadline || data.questions_deadline }
    ];

    dateFields.forEach(({ field, value }) => {
      if (value) {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          warnings.push(`Invalid date format for ${field}: ${value}`);
        }
      }
    });
  }

  private static validateNumericFields(data: Partial<SamContractResponse>, warnings: string[]) {
    const numericFields = [
      { field: 'fundingAmount', value: data.fundingAmount || data.funding_amount },
      { field: 'awardAmount', value: data.awardAmount || data.award_amount },
      { field: 'basePeriodAmount', value: data.basePeriodAmount || data.base_period_amount },
      { field: 'totalContractValue', value: data.totalContractValue || data.total_contract_value }
    ];

    numericFields.forEach(({ field, value }) => {
      if (value !== undefined && value !== null) {
        const num = parseFloat(value.toString());
        if (isNaN(num) || num < 0) {
          warnings.push(`Invalid numeric value for ${field}: ${value}`);
        }
      }
    });
  }

  private static validateNaicsCode(naicsCode: string | undefined, warnings: string[]) {
    if (naicsCode && !/^\d{6}$/.test(naicsCode)) {
      warnings.push(`Invalid NAICS code format: ${naicsCode}. Should be 6 digits.`);
    }
  }

  private static validateLocation(location: any, locationName: string, warnings: string[]) {
    if (location && typeof location === 'object') {
      if (!location.city && !location.state) {
        warnings.push(`${locationName} missing city and state`);
      }
      
      if (location.state && typeof location.state === 'string' && location.state.length > 2) {
        warnings.push(`${locationName} state should be 2-letter code, got: ${location.state}`);
      }
      
      if (location.zip && !/^\d{5}(-\d{4})?$/.test(location.zip)) {
        warnings.push(`${locationName} invalid ZIP code format: ${location.zip}`);
      }
    }
  }

  static validateContractForStorage(contract: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Critical fields for database storage
    if (!contract.notice_id) {
      errors.push('notice_id is required for database storage');
    }
    
    if (!contract.title) {
      errors.push('title is required for database storage');
    }
    
    if (!contract.agency) {
      errors.push('agency is required for database storage');
    }
    
    if (!contract.naics_code && !contract.naicsCode) {
      errors.push('naics_code is required for database storage');
    }
    
    if (!contract.status) {
      errors.push('status is required for database storage');
    }
    
    if (!contract.posted_date && !contract.postedDate) {
      errors.push('posted_date is required for database storage');
    }
    
    // Note: due_date is NOT required - some contract types (like awards) don't have response deadlines
    // We'll set a default value in the transformer instead

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}